"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, MessageSquareQuote, Quote, User } from "lucide-react";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { A11y, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/a11y";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function useRobotoLang(): "vi" | "en" {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const readLang = () => {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") {
        setLang(stored);
      }
    };
    readLang();
    const handleCustomChange = () => readLang();
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  return lang;
}
function SwiperControls() {
  const swiper = useSwiper();
  return <div className="mt-8 flex items-center justify-center gap-3">
      <Button variant="outline" size="icon" aria-label="Previous slide" onClick={() => swiper.slidePrev()} className="rounded-full border-border bg-card shadow-xs transition-colors hover:border-primary hover:text-primary">
        <ChevronLeft className="size-4" />
      </Button>
      <Button variant="outline" size="icon" aria-label="Next slide" onClick={() => swiper.slideNext()} className="rounded-full border-border bg-card shadow-xs transition-colors hover:border-primary hover:text-primary">
        <ChevronRight className="size-4" />
      </Button>
    </div>;
}
function TestimonialsConsumer({
  lang
}: {
  lang: "vi" | "en";
}) {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map(item => <div key={item} className="flex h-64 flex-col justify-between rounded-xl border border-border/80 bg-card p-6 shadow-xs animate-pulse">
            <div className="space-y-3">
              <div className="h-4 w-20 rounded bg-muted" />
              <div className="h-4 w-full rounded bg-muted" />
              <div className="h-4 w-4/5 rounded bg-muted" />
            </div>
            <div className="flex items-center gap-3 pt-4">
              <div className="size-11 rounded-full bg-muted" />
              <div className="space-y-1.5">
                <div className="h-4 w-28 rounded bg-muted" />
                <div className="h-3 w-20 rounded bg-muted" />
              </div>
            </div>
          </div>)}
      </div>;
  }
  if (error || posts.length === 0) {
    return <div className="rounded-2xl border border-dashed border-border bg-card/60 p-8 text-center sm:p-12">
        <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <MessageSquareQuote className="size-7 text-primary/70" />
        </div>
        <h3 className="mt-4 font-serif text-xl font-bold tracking-tight text-foreground sm:text-2xl">
          {lang === "vi" ? "Chưa có phản hồi mới" : "No testimonials yet"}
        </h3>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          {lang === "vi" ? "Các phản hồi và đánh giá từ phụ huynh đang được cập nhật qua hệ thống." : "Parent reviews and feedback are currently being populated via the admin system."}
        </p>
      </div>;
  }
  return <Swiper modules={[Navigation, Pagination, A11y]} spaceBetween={24} slidesPerView={1} loop={posts.length > 3} pagination={{
    clickable: true,
    bulletClass: "inline-block size-2 rounded-full bg-muted-foreground/30 mx-1 cursor-pointer transition-all",
    bulletActiveClass: "!bg-primary !w-6"
  }} breakpoints={{
    640: {
      slidesPerView: 1.5
    },
    768: {
      slidesPerView: 2
    },
    1024: {
      slidesPerView: 3
    }
  }} a11y={{
    prevSlideMessage: lang === "vi" ? "Đánh giá trước" : "Previous review",
    nextSlideMessage: lang === "vi" ? "Đánh giá kế tiếp" : "Next review"
  }} className="pb-4">
      {posts.map((post, i) => {
      const rawAcf = (post as unknown as {
        acf?: Record<string, string>;
      }).acf || {};
      const custom = post.customFields || {};
      const parentName = rawAcf.parent_name || custom.parent_name as string || post.title;
      const avatarUrl = rawAcf.avatar || custom.avatar as string || post.featuredImage || "";
      const quote = rawAcf.quote || custom.quote as string || post.excerpt || "";
      const programName = rawAcf.program_name || custom.program_name as string || "";
      const studentLabel = rawAcf.student_label || custom.student_label as string || "";
      return <SwiperSlide key={post.id} data-index={i} className="h-auto">
            <Card className="group relative flex h-full flex-col justify-between overflow-hidden rounded-xl border border-border bg-card p-6 shadow-md transition-all hover:border-primary/40 hover:shadow-lg">
              {/* Subtle architectural grid background accent */}
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:16px_16px] opacity-25" />

              <CardContent className="relative z-10 flex h-full flex-col justify-between p-0">
                <div>
                  <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-3">
                    <Badge variant="outline" className="border-accent/40 bg-accent/10 text-accent font-medium text-[11px]">
                      {lang === "vi" ? "Nội dung minh họa" : "Illustrative Content"}
                    </Badge>
                    <Quote className="size-6 text-primary/30 transition-colors group-hover:text-primary/60" />
                  </div>

                  {/* Prominent pull-quote */}
                  <blockquote className="mt-4 font-sans text-base leading-relaxed text-foreground/90 italic">
                    "{quote}"
                  </blockquote>
                </div>

                <div className="mt-6 border-t border-border/60 pt-4">
                  <div className="flex items-center gap-3">
                    <SafeHydrate><Avatar className="size-11 border border-border bg-muted">
                      {avatarUrl ? <AvatarImage src={avatarUrl} alt={parentName} /> : null}
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        <User className="size-5" />
                      </AvatarFallback>
                    </Avatar></SafeHydrate>

                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-sans text-sm font-semibold text-foreground">
                        {parentName}
                      </h3>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                        {studentLabel && <span className="font-medium text-secondary-foreground">
                            {studentLabel}
                          </span>}
                        {studentLabel && programName && <span>•</span>}
                        {programName && <span className="truncate text-muted-foreground">
                            {programName}
                          </span>}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </SwiperSlide>;
    })}

      <SwiperControls />
    </Swiper>;
}
export default function ParentTestimonials() {
  const lang = useRobotoLang();
  const wpQuery = new WP_Query({
    post_type: "parent_testimonial",
    per_page: 6,
    status: ["publish"]
  });
  return <section id="parenttestimonials" data-nav="light" className="relative overflow-hidden bg-background py-16 text-foreground md:py-24">
      {/* Decorative Blueprint spec line accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div aria-hidden="true" className="pointer-events-none absolute -left-20 top-1/3 size-72 rounded-full bg-secondary/10 blur-xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-20 top-2/3 size-72 rounded-full bg-accent/10 blur-xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Centered precision spec header */}
        <div className="mx-auto mb-12 max-w-3xl text-center md:mb-16">
          <div className="mb-3 inline-flex items-center gap-2">
            <span className="size-1.5 rounded-full bg-primary" />
            <span className="h-px w-8 bg-primary/40" />
            <span className="font-mono text-xs font-semibold tracking-wider text-primary uppercase">
              {lang === "vi" ? "ĐÁNH GIÁ TỪ PHỤ HUYNH" : "TESTIMONIALS"}
            </span>
            <span className="h-px w-8 bg-primary/40" />
            <span className="size-1.5 rounded-full bg-primary" />
          </div>

          <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            {lang === "vi" ? "PHỤ HUYNH NÓI GÌ VỀ ROBOTO?" : "What Parents Say About ROBOTO"}
          </h2>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            {lang === "vi" ? "Lắng nghe trải nghiệm và hành trình học tập của các bạn nhỏ tại ROBOTO." : "Hear about the experience and learning journey of ROBOTO's young students."}
          </p>
        </div>

        {/* Content wrapped in PostsProvider */}
        <PostsProvider wp_query={{
        post_type: "parent_testimonial",
        per_page: 6,
        status: ["publish"]
      }}>
          <TestimonialsConsumer lang={lang} />
        </PostsProvider>
      </div>
    </section>;
}